<template>
  <Dialog
    v-model:visible="visibleValue"
    modal
    :draggable="false"
    dismissable-mask
    :header="$t('components.structural.profile.dialog.title')"
    :style="{ width: '100%', maxWidth: '480px' }"
    @show="onDialogShow"
    @hide="onDialogHide"
  >
    <div
      v-if="isFetchingMember || !hasFetchedMandatoryData"
      class="flex justify-center"
    >
      <ProgressSpinner
        style="width: 64px; height: 64px"
        stroke-width="3"
      />
    </div>

    <template v-else>
      <div class="mb-8 flex items-end">
        <Avatar
          :image="member.avatar?.photoURL"
          size="xlarge"
          shape="circle"
        />
        <StorageFileUpload
          :path="
            Paths.Organizations.Members.Uid(selectedOrganization.id, member.id)
          "
          file-name="avatar"
          class="p-2"
          @uploaded="onAvatarUploaded"
        />
      </div>

      <FormsForm
        :schema="schema"
        :initial-values="initialValues"
        :on-submit="updateMemberOrganizationProfile"
        :submit-label="$t('common.cta.save')"
      >
        <FormsFieldsInputText
          name="displayName"
          :label="
            $t('components.structural.profile.dialog.form.display-name.label')
          "
          :placeholder="
            $t(
              'components.structural.profile.dialog.form.display-name.placeholder',
            )
          "
        />

        <component
          :is="additionalFormFields"
          v-if="hasAdditionalFormFields"
        />
      </FormsForm>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { object, string } from 'yup'
import { doc, setDoc } from '@firebase/firestore'
import { getDownloadURL } from '@firebase/storage'
import { ignoreUndefinedProperties } from '~/utils/firebase'
import type { StorageFileUploadItem } from '~/types/components/ui/storage-file-upload'
import { useOrganizationsStore } from '~/store/organizations'
import { Collections } from '~/constants/firebase'
import { Paths } from '~/constants/firebase/storage'
import { AdditionalFormsFields } from '~/constants/components/additional'
import { getAdditionalFormFields } from '~/utils/components'
import type { OrganizationMember } from '~/types/organizations/member'
import StorageFileUpload from '~/components/ui/StorageFileUpload.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})
const emits = defineEmits(['hide'])

const visibleValue = computed({
  get: () => props.visible,
  set: () => emits('hide'),
})

const organizationsStore = useOrganizationsStore()
const { selectedOrganization } = storeToRefs(organizationsStore)

const { hasAdditionalFormFields, additionalFormFields, schema }
  = getAdditionalFormFields(
    AdditionalFormsFields.Profile,
    object().shape({
      displayName: string().min(3).required(),
    }),
  )
const initialValues = ref({})
const hasFetchedMandatoryData = ref(false)

const currentUser = await getCurrentUser()
const { data: member, pending: isFetchingMember } = useDocument<OrganizationMember>(
  computed(() => doc(
    useGetFirestore(),
    Collections.Organizations.members(selectedOrganization.value.id),
    currentUser.uid,
  )),
  {
    wait: true,
    ssrKey: `${Collections.Organizations.members(selectedOrganization.value.id)}-${currentUser.uid}`,
  },
)

const onDialogShow = () => {
  initialValues.value = {
    displayName: member.value.displayName,
    ...(hasAdditionalFormFields
      ? (() => {
          const { avatar, role, member: memberRef, invitedBy, ...rest } = member.value
          return rest
        })()
      : {}),
  }
  hasFetchedMandatoryData.value = true
}
const onDialogHide = () => {
  initialValues.value = {}
  hasFetchedMandatoryData.value = false
}

const onAvatarUploaded = async (storageFileUploadItem: StorageFileUploadItem) => {
  const photoURL = await getDownloadURL(storageFileUploadItem.snapshot.ref)
  await updateMemberOrganizationProfile({
    avatar: {
      photoURL,
    },
  })
}

// TODO: Fix when avatar change
const updateMemberOrganizationProfile = async (values: any) => {
  await setDoc(
    doc(
      useGetFirestore(),
      Collections.Organizations.members(selectedOrganization.value.id),
      member.value.id,
    ),
    ignoreUndefinedProperties(values),
    { merge: true },
  )
}
</script>
